import { absoluteUrl } from "@/lib/seo/site";
import { UTM_CONTENT_GOOGLE_CLASSROOM, appendUtmParams } from "@/lib/utm";

/** Classroom-optimized default title: clear, scannable, under ~60 chars. */
export const DEFAULT_CLASSROOM_TITLE = "PlayMath.org - Math Games for Kids";

/** Default body for Classroom: teacher-voice, kid audience; no payment/pitch language. */
export const DEFAULT_CLASSROOM_BODY =
  "We're doing math the playful way… games! Jump in now and play a game.";

/**
 * Only well-formed http(s) URLs count as absolute; otherwise join with site base.
 * Used when building Classroom share links so strings like `httpfoo` are not treated as absolute.
 */
export function resolveClassroomShareUrl(url: string): string {
  const t = url.trim();
  try {
    const u = new URL(t);
    if (u.protocol === "http:" || u.protocol === "https:") {
      return u.toString();
    }
  } catch {
    // relative path or invalid — use site base
  }
  return absoluteUrl(t);
}

/** URL to open Google Classroom share flow. Use for custom buttons. Pre-fills title and body when provided. */
export function classroomShareUrl(
  url: string,
  options?: { title?: string; body?: string }
): string {
  const full = resolveClassroomShareUrl(url);
  const params = new URLSearchParams({ url: full });
  if (options?.title) params.set("title", options.title);
  if (options?.body) params.set("body", options.body);
  return `https://classroom.google.com/share?${params.toString()}`;
}

/**
 * Classroom share URL for a page already tagged for game/site share.
 * Adds/overwrites `utm_content=google_classroom` on the inner URL; keeps other params.
 */
export function classroomShareUrlForPage(
  pageUrl: string,
  options?: { title?: string; body?: string }
): string {
  return classroomShareUrl(
    appendUtmParams(pageUrl, { utm_content: UTM_CONTENT_GOOGLE_CLASSROOM }),
    options
  );
}
